<template>
	<div class="relative h-full">
		<Carousel
			class="size-full"
			:opts="{
				loop: true
			}"
			:plugins="[WheelGesturesPlugin()]"
			@init-api="setupCarouselApi"
		>
			<CarouselContent class="h-full">
				<CarouselItem
					v-for="(image, index) in limitedImages"
					:key="'watermarkUrl' in image ? image.watermarkUrl : image.url"
					@click="handleItemClick()"
				>
					<slot :image="image" :index="index" />
				</CarouselItem>
			</CarouselContent>

			<template v-if="limitedImages?.length > 1">
				<CarouselPrevious
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="handleImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronLeft class="size-8 stroke-2" />
				</CarouselPrevious>

				<CarouselNext
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="handleImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronRight class="size-8 stroke-2" />
				</CarouselNext>
			</template>
		</Carousel>

		<div
			v-if="limitedImages?.length > 1"
			:class="
				cn(
					dotsVariants?.({
						size: variant === 'fullFace' ? 'lg' : 'sm'
					}),
					'opacity-100 group-hover/card:opacity-100 md:opacity-0'
				)
			"
		>
			<button
				v-for="(_, index) in limitedImages"
				:key="`card-carousel-index:${index}`"
				class="size-1 min-w-1 cursor-pointer"
				:class="isCurrentItem(index) ? 'bg-white' : 'bg-white/50'"
				@click="$event => goTo($event, index)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {
	CardCarouselProps,
	ImageClickedEvent,
	ImageScrolledEvent
} from '.';
import type { CarouselApi } from '../../ui/carousel';

import { cn } from '@/utils';
import { Carousel } from '#components';
import { isNumber } from '~~/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { CarouselNext, CarouselPrevious } from '../../ui/carousel';

const props = defineProps<CardCarouselProps>();
const emit = defineEmits<{
	'image-clicked': [ImageClickedEvent];
	'image-scrolled': [ImageScrolledEvent];
}>();

const carouselApi = ref<CarouselApi | null>(null);

const currentItem = ref<number>(0);
const clickCounts = ref(0);

const limitedImages = computed(() => props.images.slice(0, 5));

function setupCarouselApi(api: CarouselApi) {
	carouselApi.value = api;
	carouselApi.value?.on('select', () => {
		currentItem.value = carouselApi.value?.selectedScrollSnap() || 0;
	});
}

function isCurrentItem(index: number): boolean {
	return currentItem.value === index;
}

function goTo(event: MouseEvent, index: number) {
	event.stopPropagation();
	event.preventDefault();
	carouselApi.value?.scrollTo(index);
}

function handleItemClick() {
	const image = props.images[currentItem.value];
	const photoUrl =
		isNumber(currentItem.value) && image
			? ('watermarkUrl' in image && image.watermarkUrl) ||
				('url' in image ? image.url : undefined)
			: undefined;

	const photoPosition = isNumber(currentItem.value)
		? currentItem.value + 1
		: undefined;

	emit('image-clicked', {
		currentItem: currentItem.value,
		photoUrl,
		photoPosition
	});
}

function handleImageScrollClick() {
	clickCounts.value += 1;

	emit('image-scrolled', {
		clickCount: clickCounts.value
	});
}
</script>
